import { GuideRunPouchContent, PhaseStatus } from '@libs/shared/interfaces/couch/models/guide-run';
import { GuideDto } from '@libs/shared/interfaces/guide/dtos';
import { GuideSource } from '@libs/shared/interfaces/guide/models';
import { isAfter, isBefore, isSameDay, parseISO, startOfSecond } from 'date-fns';

/**
 * Calculates the currently active guide run phase at the given point in time.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {Date} date The point in time
 * @param {boolean} checkDates Whether to check the dates or only if started / ended is set.
 * @returns {PhaseStatus | undefined}
 */
export function getActiveGuideRunPhase(
  guideRun: Pick<GuideRunPouchContent, 'phases'>,
  date: Date,
  checkDates = true,
): PhaseStatus | undefined {
  if (!checkDates) {
    return guideRun.phases.find((phase) => phase.started && !phase.ended);
  }

  const dateStartOfSecond = startOfSecond(date);

  return guideRun.phases.find((phase) => {
    const startedIsPastOrNow = phase.started && !isAfter(parseISO(phase.started), dateStartOfSecond);
    const endedIsFutureOrUndefined = !phase.ended || !isBefore(parseISO(phase.ended), dateStartOfSecond);

    return startedIsPastOrNow && endedIsFutureOrUndefined;
  });
}

/**
 *
 * @param {GuideRunPouchContent} guideRun the guide run document
 * @param {string} phaseId the id of the phase
 * @returns {PhaseStatus | undefined}
 */
export function getPhaseById(guideRun: Pick<GuideRunPouchContent, 'phases'>, phaseId: string): PhaseStatus | undefined {
  return guideRun.phases.find((phase) => phase.id === phaseId);
}

/**
 * Calculates the currently active guide run phase at the given point in time.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {Date} date The point in time
 * @param {boolean} checkDates Whether to check the dates or only if started / ended is set.
 * @returns {PhaseStatus | undefined}
 */
export function getActiveGuideRunPhaseSameDay(
  guideRun: Pick<GuideRunPouchContent, 'phases'>,
  date: Date,
  checkDates = true,
): PhaseStatus | undefined {
  if (!checkDates) {
    return guideRun.phases.find((phase) => phase.started && !phase.ended);
  }

  const dateStartOfSecond = startOfSecond(date);

  return guideRun.phases.find((phase) => {
    const startedIsPastOrToday =
      phase.started &&
      (isSameDay(date, parseISO(phase.started)) || !isAfter(parseISO(phase.started), dateStartOfSecond));
    const endedIsFutureOrUndefined = !phase.ended || !isBefore(parseISO(phase.ended), dateStartOfSecond);

    return startedIsPastOrToday && endedIsFutureOrUndefined;
  });
}

/**
 * Calculates the guide run phases at the given point in time.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {Date} date The point in time
 * @param {boolean} checkDates Whether to check the dates or only if started / ended is set.
 * @returns {PhaseStatus | undefined}
 */
export function getGuideRunPhasesSameDay(
  guideRun: Pick<GuideRunPouchContent, 'phases'>,
  date: Date,
  checkDates = true,
): PhaseStatus[] {
  if (!checkDates) {
    return guideRun.phases.filter((phase) => phase.started && !phase.ended);
  }

  const dateStartOfSecond = startOfSecond(date);

  return guideRun.phases.filter((phase) => {
    const startedIsPastOrToday =
      phase.started &&
      (isSameDay(date, parseISO(phase.started)) || !isAfter(parseISO(phase.started), dateStartOfSecond));
    const endedIsFutureOrUndefined =
      !phase.ended || isSameDay(date, parseISO(phase.ended)) || !isBefore(parseISO(phase.ended), dateStartOfSecond);

    return startedIsPastOrToday && endedIsFutureOrUndefined;
  });
}

/**
 * Calculates the next guide run phase in the list at the given point in time.
 * It is strictly sticking to the order in the list and assumes that phases are done
 * in sequential order.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {Date} date The point in time
 * @param {boolean} checkDates Whether to check the dates or only if started / ended is set.
 * @returns {PhaseStatus[]}
 */
export function getNextPhaseInSequentialList(
  guideRun: Pick<GuideRunPouchContent, 'phases'>,
  date: Date,
  checkDates = true,
): PhaseStatus | undefined {
  return guideRun.phases.find((phase) => !phase.started || (checkDates && isAfter(parseISO(phase.started), date)));
}

/**
 * Calculates the next guide run phase in the list relative to the given phase.
 * It is strictly sticking to the order in the list and assumes that phases are done
 * in sequential order.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {PhaseStatus} phase The phase to get the next one for
 * @returns {PhaseStatus[]}
 */
export function getNextPhaseInList(
  guideRun: Pick<GuideRunPouchContent, 'phases'>,
  phase: PhaseStatus,
): PhaseStatus | undefined {
  const index = guideRun.phases.indexOf(phase);

  return guideRun.phases.length > index ? guideRun.phases[index + 1] : undefined;
}

/**
 * Calculates the last phase id of a guide.
 *
 * @param {GuideDto} guide The guide document
 * @returns {string}
 */
export function getLastPhaseIdInGuide(
  guide: Pick<GuideDto, 'guidePhases'> | Pick<GuideSource, 'guidePhases'>,
): string | undefined {
  if (!guide.guidePhases) {
    return undefined;
  }

  const index = guide.guidePhases.length - 1;
  const phaseId = guide.guidePhases[index].guidePhase?.internalName;

  return phaseId;
}

/**
 * Calculates the last phase of a guide.
 *
 * @param {GuideDto} guide The guide document
 * @returns {PhaseStatus}
 */
export function getLastPhaseInGuide(
  guide: Pick<GuideDto, 'guidePhases'> | Pick<GuideSource, 'guidePhases'>,
  guideRun: Pick<GuideRunPouchContent, 'phases'>,
): PhaseStatus | undefined {
  const phaseId = getLastPhaseIdInGuide(guide);

  return phaseId ? guideRun.phases.find((phase) => phase.id === phaseId) : undefined;
}

/**
 * Calculates all upcoming guide run phases at the given point in time.
 * The list is ordered by started time ascending.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {Date} date The point in time
 * @returns {PhaseStatus[]}
 */
export function getNextScheduledPhases(guideRun: Pick<GuideRunPouchContent, 'phases'>, date: Date): PhaseStatus[] {
  return guideRun.phases
    .filter((phase) => phase.started && isAfter(parseISO(phase.started), date))
    .sort((a, b) => parseISO(a.started!).getTime() - parseISO(b.started!).getTime());
}

/**
 * Calculates all previous guide run phases at the given point in time.
 * The list is ordered by started time ascending.
 *
 * @param {GuideRunPouchContent} guideRun The guide run document
 * @param {Date} date The point in time
 * @returns {PhaseStatus[]}
 */
export function getPreviousPhases(guideRun: Pick<GuideRunPouchContent, 'phases'>, date: Date): PhaseStatus[] {
  const dateStartOfSecond = startOfSecond(date);

  return guideRun.phases
    .filter((phase) => phase.started && phase.ended && isBefore(parseISO(phase.ended), dateStartOfSecond))
    .sort((a, b) => parseISO(a.started!).getTime() - parseISO(b.started!).getTime());
}
